// All API calls centralised. Nothing else imports fetch directly.
// BASE reads REACT_APP_API_URL env so it works locally and on Cloud Run.
const BASE = process.env.REACT_APP_API_URL !== undefined
  ? process.env.REACT_APP_API_URL
  : "http://localhost:8000";

// --- Auth helpers ---
function getAuthHeader() {
  const token = localStorage.getItem("agronav_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function authHeaders() {
  return { "Content-Type": "application/json", ...getAuthHeader() };
}

// --- Fallback NBA (offline) ---
export const FALLBACK_NBA = {
  product: "Tilt 250 EC",
  pitch: "Fungal disease pressure is high. Stock up now before farmers start asking.",
  tip: "Kharif crop at critical stage — recommend preventive spray within 72 hours.",
  promotion: "Buy 20+ units — 5% extra dealer margin this week.",
  why: "Pest alert active + stock running low + crop stage = act today."
};

// --- Auth ---
export const login = async (identifier, password) => {
  /** Supports email address OR Rep ID (e.g. REP_0203). Password always required. */
  const body = { email: identifier, password };
  const res = await fetch(`${BASE}/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body)
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json(); // { token, user: { rep_id, name, territory, … } }
};

export const signup = async (name, email, repId, password, role = "rep", district = null, state = null, territory_id = null) => {
  const res = await fetch(`${BASE}/signup`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ name, email, rep_id: repId, password, role, district, state, territory_id })
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
};

// --- Low-bandwidth detection ---
// Returns true on 2G/slow-2g or when Data Saver is enabled in the browser/OS.
export const isLowBandwidth = () => {
  const conn = navigator.connection ||
               navigator.mozConnection ||
               navigator.webkitConnection;
  if (!conn) return false;
  return conn.effectiveType === "2g" ||
         conn.effectiveType === "slow-2g" ||
         conn.saveData === true;
};

// --- Recommendations ---
export const getRecommendations = async (repId, date, district = null, aiMode = null) => {
  const token = localStorage.getItem("agronav_token");
  const dateStr = date || new Date().toISOString().split("T")[0];
  let url = `${BASE}/recommendations?rep_id=${encodeURIComponent(repId)}&date=${dateStr}`;
  if (district) url += `&district=${encodeURIComponent(district)}`;
  const mode = aiMode || localStorage.getItem("agronav_ai_mode") || "live";
  if (mode === "fast") url += `&ai_mode=fast`;
  // On 2G/Data Saver: request lite payload (~5KB vs ~50KB) — skips weather,
  // NDVI badges and LLM NBA. Backend respects ?mode=lite.
  if (isLowBandwidth()) url += `&mode=lite`;
  const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
};

// --- Visit Logs ---
export const postVisitLog = async (data) => {
  const token = localStorage.getItem("agronav_token");
  const res = await fetch(`${BASE}/visit_log`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: JSON.stringify(data)
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
};

// --- Outcomes ---
export const getOutcomes = async (repId) => {
  const res = await fetch(`${BASE}/api/outcomes?rep_id=${repId}`, {
    headers: getAuthHeader()
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json(); // { logs: [...] }
};

// --- Territory ---
export const updateTerritory = async (repId, state, district, territoryId = "") => {
  const res = await fetch(`${BASE}/api/rep/territory`, {
    method: "PATCH",
    headers: authHeaders(),
    body: JSON.stringify({ rep_id: repId, state, district, territory_id: territoryId })
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
};

// --- Profile (name + territory) ---
export const updateProfile = async ({ name, district, state }) => {
  const res = await fetch(`${BASE}/api/rep/profile`, {
    method: "PATCH",
    headers: authHeaders(),
    body: JSON.stringify({ name, district, state })
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json(); // { success, user }
};

// --- Rep profile cache ---
export const cacheRepProfile = (profile) => {
  localStorage.setItem("agronav_rep_profile", JSON.stringify({
    ...profile, cached_at: new Date().toISOString()
  }));
};
export const getCachedRepProfile = () => {
  const raw = localStorage.getItem("agronav_rep_profile");
  return raw ? JSON.parse(raw) : null;
};

// --- Recommendation cache ---
export const cacheRecommendations = (data) => {
  localStorage.setItem("agronav_recommendations", JSON.stringify({
    ...data, cached_at: new Date().toISOString()
  }));
};
export const getCachedRecommendations = () => {
  const raw = localStorage.getItem("agronav_recommendations");
  return raw ? JSON.parse(raw) : null;
};

// --- api object (backward compat for existing pages) ---
export const api = {
  login, getRecommendations, postVisitLog, getOutcomes,
  cacheRepProfile, getCachedRepProfile, cacheRecommendations, getCachedRecommendations,
  getMorningSync: async (rep_id) => api.morningSync(rep_id),
  getLogs: async (repId) => getOutcomes(repId),

  morningSync: async (rep_id = 1) => {
    const res = await fetch(`${BASE}/api/sync/morning?rep_id=${rep_id}`, {
      headers: getAuthHeader()
    });
    if (!res.ok) throw new Error("Sync failed");
    return res.json();
  },

  getNBA: async (outlet_id) => {
    try {
      const res = await fetch(`${BASE}/api/nba/${outlet_id}`, { headers: getAuthHeader() });
      if (!res.ok) return FALLBACK_NBA;
      return res.json();
    } catch {
      return FALLBACK_NBA;
    }
  },

  // FIX 5: one-sentence "Why this outlet?" explanation via LLaMA 3.3
  explainOutlet: async (retailer_id) => {
    const res = await fetch(`${BASE}/api/explain/${encodeURIComponent(retailer_id)}`, {
      headers: getAuthHeader(),
    });
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  // FIX 6: weekly acceptance-rate trend for the manager learning chart
  getRecalibrateHistory: async () => {
    const res = await fetch(`${BASE}/api/recalibrate/history`, { headers: getAuthHeader() });
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  logOutcome: async (outlet_id, result, order_value = 0, rejection_reason = null, notes = "") => {
    const res = await fetch(`${BASE}/api/visits/log`, {
      method: "POST",
      headers: authHeaders(),
      body: JSON.stringify({ outlet_id, rep_id: 1, result, order_value, rejection_reason, notes })
    });
    return res.json();
  },

  recalibrate: async (repId = "REP_0001") => {
    const id = repId || localStorage.getItem("agronav_rep_id") || "REP_0001";
    const res = await fetch(`${BASE}/api/recalibrate?rep_id=${id}`, {
      method: "POST", headers: getAuthHeader()
    });
    return res.json();
  },

  recalibrateWithExplain: async (repId, days = 30) => {
    const id = repId || localStorage.getItem("agronav_rep_id") || "REP_0001";
    const res = await fetch(
      `${BASE}/api/recalibrate/explain?rep_id=${encodeURIComponent(id)}&days=${days}`,
      { method: "POST", headers: getAuthHeader() }
    );
    return res.json();
    // Returns: { updated_outlets, moved_up, moved_down, stats, explanation: {line1, line2, line3, full_text, source} }
  },

  getAlerts: async (district = "Jalgaon") => {
    const res = await fetch(`${BASE}/api/alerts?district=${encodeURIComponent(district)}`, { headers: getAuthHeader() });
    return res.json();
  },

  dismissAlert: async (id) => {
    await fetch(`${BASE}/api/alerts/${id}/dismiss`, {
      method: "POST", headers: getAuthHeader()
    });
  },

  detectAnomaly: async (district, { category, weatherRisk, competitorStockout } = {}) => {
    const params = new URLSearchParams({ district });
    if (category) params.append("category", category);
    if (weatherRisk) params.append("weather_risk", weatherRisk);
    if (competitorStockout) params.append("competitor_stockout", "true");
    const res = await fetch(`${BASE}/api/alerts/detect?${params}`, {
      method: "POST", headers: getAuthHeader()
    });
    return res.json();
  },

  detectAllDistricts: async (topN = 5) => {
    const res = await fetch(`${BASE}/api/alerts/detect-all?top_n=${topN}`, {
      method: "POST", headers: getAuthHeader()
    });
    return res.json();
  },

  getManagerKPIs: async (territory = "Nalgonda") => {
    const res = await fetch(`${BASE}/api/manager/kpis?territory=${territory}`, {
      headers: getAuthHeader()
    });
    return res.json();
  },

  demoReset: async () => {
    const res = await fetch(`${BASE}/api/demo/reset`, { headers: getAuthHeader() });
    return res.json();
  },

  // Competitive intelligence: Places API + LLM classification
  analyzeCompetitor: async ({
    retailer_id, outlet_name, district, tehsil = "",
    lat = 17.3850, lng = 78.4867,
    rep_text_input = "",
    stock_days_remaining = 14, days_since_purchase = 7, crop_stage = "vegetative"
  }) => {
    const res = await fetch(`${BASE}/api/competitor/analyze`, {
      method: "POST",
      headers: authHeaders(),
      body: JSON.stringify({
        retailer_id, outlet_name, district, tehsil,
        lat, lng, rep_text_input,
        stock_days_remaining, days_since_purchase, crop_stage
      })
    });
    return res.json();
    // Returns: { threat_type, threat_level, competitor_name, at_risk_syngenta_products,
    //            defensive_talking_point, immediate_action, escalate_to_manager,
    //            opportunity_flag, nearby_stores, source, intel_id }
  },

  getCompetitorHistory: async (retailer_id, limit = 5) => {
    const res = await fetch(
      `${BASE}/api/competitor/history?retailer_id=${encodeURIComponent(retailer_id)}&limit=${limit}`,
      { headers: getAuthHeader() }
    );
    return res.json();
  },

  // Route optimization + morning briefing
  getMorningBrief: async (repId, { repLat, repLng, topN = 6, district } = {}) => {
    const id = repId || localStorage.getItem("agronav_rep_id") || "REP_0001";
    const body = {
      rep_id:   id,
      top_n:    topN,
      ...(repLat  != null && { rep_lat: repLat }),
      ...(repLng  != null && { rep_lng: repLng }),
      ...(district      && { district }),
    };
    const res = await fetch(`${BASE}/api/route/morning-brief`, {
      method: "POST",
      headers: authHeaders(),
      body: JSON.stringify(body),
    });
    return res.json();
    // Returns: { briefing: {line1, line2, line3, full_text, source},
    //            route: {ordered_outlet_list, total_km, total_minutes, outlet_count, route_source},
    //            weather: {...}, top_alert: str }
  },

  getRouteOptimized: async (repId, { repLat, repLng, topN = 6 } = {}) => {
    const id = repId || localStorage.getItem("agronav_rep_id") || "REP_0001";
    const body = { rep_id: id, top_n: topN,
      ...(repLat != null && { rep_lat: repLat }),
      ...(repLng != null && { rep_lng: repLng }),
    };
    const res = await fetch(`${BASE}/api/route/optimize`, {
      method: "POST",
      headers: authHeaders(),
      body: JSON.stringify(body),
    });
    return res.json();
  },

  // Weekly outcome learning (Gap 5)
  getWeeklyStats: async (district = "Jalgaon", state = "Maharashtra") => {
    const res = await fetch(
      `${BASE}/api/learning/weekly-stats?district=${encodeURIComponent(district)}&state=${encodeURIComponent(state)}`,
      { headers: getAuthHeader() }
    );
    return res.json();
    // Returns: {week_label, total_visits, sales_pct, orders_pct, product_rows, rep_rows, ...}
  },

  getWeeklyLearning: async (district = "Jalgaon", state = "Maharashtra") => {
    const res = await fetch(
      `${BASE}/api/learning/weekly-analysis?district=${encodeURIComponent(district)}&state=${encodeURIComponent(state)}`,
      { method: "POST", headers: authHeaders() }
    );
    return res.json();
    // Returns 9-field schema: {manager_alert, best_product_next_week, reps_needing_coaching,
    //   deprioritize_outlets, insight_summary, learning_action, source, raw_ctx}
  },

  // Farmer Visit Planner (Gap 6)
  getFarmerVisitPlan: async (grower) => {
    const res = await fetch(`${BASE}/api/farmers/visit-plan`, {
      method: "POST",
      headers: authHeaders(),
      body: JSON.stringify(grower),
    });
    return res.json();
    // Returns: {visit_type, recommended_product, agronomic_advice,
    //           conversation_starter, visit_reason, estimated_value, source, ...}
  },

  // Weekly visit plans (manager generates, rep views)
  generateWeeklyPlan: async (rep_id, week_start_date, manager_id = "manager") => {
    const res = await fetch(`${BASE}/api/manager/weekly-plan/generate`, {
      method: "POST",
      headers: authHeaders(),
      body: JSON.stringify({ rep_id, week_start_date, manager_id }),
    });
    return res.json();
    // Returns: { success, plan: { id, rep_id, week_label, week_start_date, week_end_date,
    //   status, total_outlets, daily_split: { monday: [...], ... } } }
  },

  approveWeeklyPlan: async (plan_id) => {
    const res = await fetch(`${BASE}/api/manager/weekly-plan/approve`, {
      method: "POST",
      headers: authHeaders(),
      body: JSON.stringify({ plan_id }),
    });
    return res.json();
    // Returns: { success, plan_id, status: "approved" }
  },

  getRepWeeklyPlan: async (rep_id) => {
    const res = await fetch(
      `${BASE}/api/rep/weekly-plan?rep_id=${encodeURIComponent(rep_id)}`,
      { headers: getAuthHeader() }
    );
    return res.json();
    // Returns: { plan: { id, rep_id, week_label, daily_split, status, today_day } | null }
  },

  listWeeklyPlans: async (rep_id = null) => {
    const qs = rep_id ? `?rep_id=${encodeURIComponent(rep_id)}` : "";
    const res = await fetch(`${BASE}/api/manager/weekly-plans${qs}`, {
      headers: getAuthHeader()
    });
    return res.json();
    // Returns: { plans: [...] }
  },

  // Role-aware AI chatbot
  sendChatMessage: async (message, role, user_id, name = "there", model = "glm-5.1", context = null) => {
    const res = await fetch(`${BASE}/api/chat`, {
      method: "POST",
      headers: authHeaders(),
      body: JSON.stringify({ message, role, user_id, name, model, context }),
    });
    return res.json();
    // Returns: { reply: string, context: {...} }
  },

  seedDemoFarmers: async () => {
    const res = await fetch(`${BASE}/api/farmers/seed-demo`, {
      method: "POST",
      headers: authHeaders(),
    });
    return res.json();
  },

  getFarmerList: async (district = "Jalgaon") => {
    const res = await fetch(
      `${BASE}/api/farmers/list?district=${encodeURIComponent(district)}`,
      { headers: getAuthHeader() }
    );
    return res.json();
  },

  // ── Admin-controlled rep/manager creation ─────────────────────────────────
  createRep: async ({ name, email, password, district, territory, phone }) => {
    const res = await fetch(`${BASE}/api/manager/create-rep`, {
      method: "POST",
      headers: authHeaders(),
      body: JSON.stringify({ name, email, password, district, territory, phone }),
    });
    return res.json();
    // Returns: { success, message, rep: {...}, login_credentials: {email, password, rep_id} }
    // HTTP 409 if email already exists
  },

  createManager: async ({ name, email, password, district, territory, phone }) => {
    const res = await fetch(`${BASE}/api/admin/create-manager`, {
      method: "POST",
      headers: authHeaders(),
      body: JSON.stringify({ name, email, password, district, territory, phone }),
    });
    return res.json();
    // Returns: { success, message, manager: {...}, login_credentials: {email, password, rep_id} }
    // HTTP 409 if email already exists; HTTP 403 if caller is not admin
  },

  // ── District list (for dropdowns) ─────────────────────────────────────────
  getDistricts: async () => {
    try {
      const res = await fetch(`${BASE}/api/manager/districts`, { headers: getAuthHeader() });
      if (!res.ok) throw new Error();
      return res.json();
    } catch {
      // Fallback static list covering Maharashtra + AP territories in dataset
      return {
        districts: [
          "Jalgaon","Aurangabad","Nashik","Pune","Ahmednagar",
          "Nalgonda","Guntur","Krishna","Kurnool","Warangal",
          "Vidisha","Bhopal","Indore","Ujjain","Jabalpur"
        ]
      };
    }
  },
};
